// export async function encryptWithPin (pin: string, plainText: string): Promise<string> {
//   const encoder = new TextEncoder()

//   const salt = crypto.getRandomValues(new Uint8Array(16))
//   const iv = crypto.getRandomValues(new Uint8Array(12)) // AES-GCM IV

//   const key = await deriveKey(pin, salt)

//   const encrypted = await crypto.subtle.encrypt(
//     { name: 'AES-GCM', iv },
//     key,
//     encoder.encode(plainText)
//   )

//   // base64 encode: [salt + iv + ciphertext]
//   const data = new Uint8Array([...salt, ...iv, ...new Uint8Array(encrypted)])
//   return btoa(String.fromCharCode(...data))
// }

// export async function decryptWithPin (pin: string, encryptedB64: string): Promise<string> {
//   const data = Uint8Array.from(atob(encryptedB64), c => c.charCodeAt(0))
//   const salt = data.slice(0, 16)
//   const iv = data.slice(16, 28)
//   const ciphertext = data.slice(28)

//   const key = await deriveKey(pin, salt)
//   const decrypted = await crypto.subtle.decrypt(
//     { name: 'AES-GCM', iv },
//     key,
//     ciphertext
//   )

//   return new TextDecoder().decode(decrypted)
// }

// async function deriveKey (pin: string, salt: Uint8Array): Promise<CryptoKey> {
//   const encoder = new TextEncoder()

//   const baseKey = await crypto.subtle.importKey(
//     'raw',
//     encoder.encode(pin),
//     { name: 'PBKDF2' },
//     false,
//     ['deriveKey']
//   )

//   return crypto.subtle.deriveKey(
//     {
//       name: 'PBKDF2',
//       salt,
//       iterations: 100_000,
//       hash: 'SHA-256'
//     },
//     baseKey,
//     { name: 'AES-GCM', length: 128 },
//     false,
//     ['encrypt', 'decrypt']
//   )
// }
// 안전한 Uint8Array <-> base64 (브라우저) 유틸
function u8ToBase64 (u8: Uint8Array): string {
  let s = ''
  const CHUNK = 0x8000
  for (let i = 0; i < u8.length; i += CHUNK) {
    s += String.fromCharCode(...u8.subarray(i, i + CHUNK))
  }
  return btoa(s)
}

function base64ToU8 (b64: string): Uint8Array {
  const bin = atob(b64)
  const u8 = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i)
  return u8
}

// ==== Constants ====
export const VERSION = 1
export const SALT_LEN = 16
export const IV_LEN = 12
export const ITERATIONS = 300_000
export const HKDF_INFO = 'app:passport-subkey:v1'

export async function encryptWithPin (pin: string, plainText: string): Promise<string> {
  const enc = new TextEncoder()
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LEN))
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN))
  const key = await deriveKey(pin, salt, ITERATIONS)

  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plainText))
  )

  // [version(1) | iterations(4) | salt | iv | ciphertext]
  const out = new Uint8Array(1 + 4 + SALT_LEN + IV_LEN + ciphertext.length)
  let o = 0
  out[o++] = VERSION
  new DataView(out.buffer, o, 4).setUint32(0, ITERATIONS, false)
  o += 4
  out.set(salt, o)
  o += SALT_LEN
  out.set(iv, o)
  o += IV_LEN
  out.set(ciphertext, o)

  return u8ToBase64(out)
}

export async function decryptWithPin (pin: string, b64: string): Promise<string> {
  const data = base64ToU8(b64)
  if (data.length < 1 + 4 + SALT_LEN + IV_LEN + 1) throw new Error('Invalid payload')

  let o = 0
  const version = data[o++]
  if (version !== VERSION) throw new Error('Unsupported version')

  const iters = new DataView(data.buffer, o, 4).getUint32(0, false)
  o += 4
  const salt = data.subarray(o, o + SALT_LEN)
  o += SALT_LEN
  const iv = data.subarray(o, o + IV_LEN)
  o += IV_LEN
  const ciphertext = data.subarray(o)

  const key = await deriveKey(pin, salt, iters)

  try {
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext)
    return new TextDecoder().decode(pt)
  } catch {
    throw new Error('Wrong PIN or corrupted data')
  }
}

async function deriveKey (pin: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const enc = new TextEncoder()
  const baseKey = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 }, // 가능하면 256bit
    false,
    ['encrypt', 'decrypt']
  )
}

// ==== Stable JSON (deterministic) ====
export function stableStringify (obj: any): string {
  if (obj === null || typeof obj !== 'object') return JSON.stringify(obj)
  if (Array.isArray(obj)) return '[' + obj.map(stableStringify).join(',') + ']'
  const keys = Object.keys(obj).sort()
  return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') + '}'
}

function toPassportBytes (passport: unknown): Uint8Array {
  // Accept string or object; normalize deterministically
  const enc = new TextEncoder()
  try {
    if (typeof passport === 'string') {
      // If string, attempt to parse to object then stable stringify
      const parsed = JSON.parse(passport)
      return enc.encode(stableStringify(parsed))
    }
  } catch {
    // If parsing fails, fall back to original string bytes
    return enc.encode(String(passport))
  }
  return enc.encode(stableStringify(passport))
}

// ==== Passport-derived key (for encrypting OTHER data) ====
// Design: IKM = stable, deterministic bytes of passport JSON
// HKDF salt: random per-token; stored alongside ciphertext
// HKDF info: constant domain label
async function derivePassportSubkey (passportBytes: Uint8Array, hkdfSalt: Uint8Array): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey('raw', passportBytes, 'HKDF', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: hkdfSalt, info: new TextEncoder().encode(HKDF_INFO) },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

// Encrypt arbitrary data using ONLY the decrypted passport as key material
// Token layout: [VER(1) | hkdfSalt(16) | iv(12) | ciphertext]
export async function encryptWithPassport (passport: unknown, plainText: string): Promise<string> {
  const passportBytes = toPassportBytes(passport)
  const hkdfSalt = crypto.getRandomValues(new Uint8Array(SALT_LEN))
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN))
  const subkey = await derivePassportSubkey(passportBytes, hkdfSalt)

  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      subkey,
      new TextEncoder().encode(plainText)
    )
  )

  const out = new Uint8Array(1 + SALT_LEN + IV_LEN + ct.length)
  let o = 0
  out[o++] = VERSION
  out.set(hkdfSalt, o)
  o += SALT_LEN
  out.set(iv, o)
  o += IV_LEN
  out.set(ct, o)
  return u8ToBase64(out)
}

export async function decryptWithPassport (passport: unknown, tokenB64: string): Promise<string> {
  const data = base64ToU8(tokenB64)
  if (data.length < 1 + SALT_LEN + IV_LEN + 1) throw new Error('Invalid token')

  let o = 0
  const version = data[o++]
  if (version !== VERSION) throw new Error('Unsupported version')

  const hkdfSalt = data.subarray(o, o + SALT_LEN)
  o += SALT_LEN
  const iv = data.subarray(o, o + IV_LEN)
  o += IV_LEN
  const ct = data.subarray(o)

  const passportBytes = toPassportBytes(passport)
  const subkey = await derivePassportSubkey(passportBytes, hkdfSalt)

  try {
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, subkey, ct)
    return new TextDecoder().decode(pt)
  } catch (e: any) {
    alert(e)
    throw new Error('Wrong passport or corrupted data')
  }
}

// ==== Usage sketch ====
// 1) 사용자 입력 PIN으로 여권 암호 해제
// const passportJsonStr = await decryptWithPin(pin, passportToken)
// 2) 해제된 여권(JSON 문자열 or 객체)로 다른 데이터 암/복호
// const token = await encryptWithPassport(passportJsonStr, 'secret')
// const plain = await decryptWithPassport(passportJsonStr, token)
